import axios from 'axios';
import { useState, useEffect } from 'react';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const client = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
client.interceptors.request.use(config => {
  const token = localStorage.getItem('sharetime_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect to /login on 401
client.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('sharetime_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

/**
 * Data-fetching hook. Re-fetches when `path` changes.
 * Returns { data, loading, error, refetch }.
 */
export function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    client.get(path)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  const refetch = () => setTick(t => t + 1);
  return { data, loading, error, refetch };
}

// Named helpers — used in components and AuthContext
export const auth = {
  register: (data) => client.post('/auth/register', data),
  login:    (data) => client.post('/auth/login',    data),
};

export const users = {
  me:              ()     => client.get('/users/me'),
  update:          (data) => client.put('/users/me', data),
  getOnboarding:   ()     => client.get('/users/me/stripe-onboarding'),
};

export const listings = {
  browse:       (params) => client.get('/listings', { params }),
  getById:      (id)     => client.get(`/listings/${id}`),
  create:       (data)   => client.post('/listings', data),
  update:       (id, data) => client.put(`/listings/${id}`, data),
  activate:     (id)     => client.post(`/listings/${id}/activate`),
  getOwnerMine: ()       => client.get('/listings/owner/mine'),
  getPriceAlert:    (id)             => client.get(`/listings/${id}/price-alert`),
  setPriceAlert:    (id, targetPrice) => client.post(`/listings/${id}/price-alert`, { targetPrice }),
  removePriceAlert: (id)             => client.delete(`/listings/${id}/price-alert`),
};

export const payments = {
  purchase:        (listingId)     => client.post(`/payments/purchase/${listingId}`),
  cancel:          (transactionId) => client.post(`/payments/cancel/${transactionId}`),
  myTransactions:  ()              => client.get('/payments/transactions'),
  ownerTransactions: ()            => client.get('/payments/owner/transactions'),
};

export const notifications = {
  getAll:     ()   => client.get('/notifications'),
  markRead:   (id) => client.put(`/notifications/${id}/read`),
  markAllRead: ()  => client.put('/notifications/read-all'),
};

export const properties = {
  getAll:              ()                    => client.get('/properties'),
  getById:             (id)                  => client.get(`/properties/${id}`),
  search:              (q, fallback = false) => client.get('/properties/search', { params: { q, ...(fallback && { fallback: 'true' }) } }),
  getForSale:          (id)                  => client.get(`/properties/${id}/for-sale`),
  getAllForSale:       ()                    => client.get('/properties/for-sale'),
  createForSale:       (id, data)            => client.post(`/properties/${id}/for-sale`, data),
  getRoomDescriptions: (id)                  => client.get(`/properties/${id}/room-descriptions`),
  getWeekDates:        (unit, week)          => client.get('/properties/week-dates', { params: { unit, week } }),
  reportResortEmail:   (id)                  => client.post(`/properties/${id}/report-resort-email`),
};

export const forSale = {
  remove: (id) => client.delete(`/for-sale/${id}`),
};

export const guestPhotos = {
  getAll:  (propertyId, category) => client.get(`/properties/${propertyId}/guest-photos`, { params: category ? { category } : {} }),
  upload:  (propertyId, data)     => client.post(`/properties/${propertyId}/guest-photos`, data),
  approve: (propertyId, photoId)  => client.patch(`/properties/${propertyId}/guest-photos/${photoId}/approve`),
};

export const uploadApi = {
  images: (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('images', f));
    return client.post('/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const templates = {
  getAll:  ()         => client.get('/templates'),
  create:  (data)     => client.post('/templates', data),
  update:  (id, data) => client.put(`/templates/${id}`, data),
  remove:  (id)       => client.delete(`/templates/${id}`),
};

export const favorites = {
  getAll: ()           => client.get('/favorites'),
  add:    (propertyId) => client.post(`/favorites/${propertyId}`),
  remove: (propertyId) => client.delete(`/favorites/${propertyId}`),
};

export const waitlist = {
  join: (email) => client.post('/waitlist', { email }),
};

export const messages = {
  getConversations: ()               => client.get('/messages'),
  getThread:        (listingId)      => client.get(`/messages/${listingId}`),
  send:             (listingId, body) => client.post(`/messages/${listingId}`, { body }),
  getForSaleThread: (forSaleListingId)       => client.get(`/messages/for-sale/${forSaleListingId}`),
  sendForSale:      (forSaleListingId, body) => client.post(`/messages/for-sale/${forSaleListingId}`, { body }),
};

export default client;
